import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container, Stack, Text, Paper, Group, Box, Loader, Button, Progress, Badge, Divider } from '@mantine/core'
import { IconBrain, IconArrowLeft, IconRefresh, IconAlertTriangle, IconChartRadar, IconTrendingUp } from '@tabler/icons-react'
import { useAnalysis } from '../hooks/useAnalysis'
import { AdaptScoreRing } from '../components/analysis/AdaptScoreRing'
import { ResponseProfileCard } from '../components/analysis/ResponseProfileCard'
import { ObjectiveProgress } from '../components/analysis/ObjectiveProgress'

import { DataCaveatBanner } from '../components/analysis/DataCaveatBanner'
import { ChoiceBreakdown } from '../components/analysis/ChoiceBreakdown'
import { RepeatComparison } from '../components/analysis/RepeatComparison'
import { LivingSkillCard } from '../components/analysis/LivingSkillCard'
import { SectionHeader } from '../components/analysis/SectionHeader'
import { MarkdownRenderer } from '../components/common/MarkdownRenderer'

/* ─── Text Response Themes ─── */

function TextInputThemes({ themes = [] }) {
  if (themes.length === 0) return null

  return (
    <Stack gap="sm">
      {themes.map((item, i) => (
        <Box
          key={i}
          style={{
            padding: '14px 16px',
            borderRadius: 8,
            background: 'rgba(10, 22, 40, 0.5)',
            border: '1px solid rgba(0, 255, 255, 0.08)',
          }}
        >
          <Stack gap="sm">
            <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600} tt="uppercase" lts={1}>
              {item.beat_name}
            </Text>

            <Text
              size="sm"
              c="cyan.2"
              ff="Rajdhani, sans-serif"
              fw={500}
              style={{
                background: 'rgba(0, 229, 255, 0.04)',
                padding: '8px 12px',
                borderRadius: 4,
                borderLeft: '3px solid rgba(0, 229, 255, 0.4)',
              }}
            >
              "{item.player_text}"
            </Text>

            {item.engaged !== false && item.themes?.length > 0 && (
              <Group gap="xs">
                {item.themes.map((theme, j) => (
                  <Badge key={j} size="xs" variant="light" color="cyan">
                    {theme}
                  </Badge>
                ))}
              </Group>
            )}

            {item.observation && (
              <Text size="sm" c="gray.4" lh={1.6}>
                {item.observation}
              </Text>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  )
}

/* ─── Story Debrief Content ─── */

const EXPLORE_BASE_URL = 'https://bubotexplore.vercel.app'

function StoryDebrief({ analysis, sessionId }) {
  const navigate = useNavigate()
  const {
    // Server-computed factual data
    session_summary,
    data_caveat,
    story_name,
    completed_at,
    attempt_number,
    // AI-generated evidence-locked content
    choice_analysis = [],
    text_input_themes = [],
    learning_objectives = [],

    observed_tendencies,
    repeat_comparison,
    // Legacy support
    adapt_scores,
    response_profile,
    adapt_narrative,
    living_skills = [],
  } = analysis

  // Detect if this is the new story analysis format or legacy full_analysis
  const isNewFormat = !!session_summary || !!data_caveat || choice_analysis.length > 0
  const primary = session_summary?.primary_tendency || response_profile?.primary

  return (
    <Stack gap="lg">
      {/* 1. Header Banner */}
      <Paper
        p="lg"
        className="glass"
        style={{
          borderColor: 'rgba(0, 229, 255, 0.2)',
          textAlign: 'center',
        }}
      >
        <Text
          size="xs"
          c="dimmed"
          ff="Orbitron, sans-serif"
          fw={600}
          tt="uppercase"
          lts={2}
          mb={4}
        >
          Session Debrief
        </Text>
        <Text
          size="xl"
          fw={700}
          ff="Orbitron, sans-serif"
          c="cyan.3"
          className="glow-text-cyan"
        >
          {story_name || 'Story Analysis'}
        </Text>
        <Group gap="sm" justify="center" mt={4}>
          {completed_at && (
            <Text size="xs" c="dimmed">
              Completed {new Date(completed_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </Text>
          )}
          {attempt_number > 1 && (
            <Badge size="sm" variant="light" color="cyan">
              Attempt {attempt_number}
            </Badge>
          )}
        </Group>
      </Paper>

      {/* 2. Data Caveat Banner */}
      <DataCaveatBanner caveat={data_caveat} />

      {/* 3. Profile Signal Pentagon (counts mode) or ADAPT Pentagon (legacy) */}
      {isNewFormat && session_summary?.signal_breakdown && (
        <Paper p="lg" className="glass">
          <Text
            size="xs"
            c="dimmed"
            ff="Orbitron, sans-serif"
            fw={600}
            tt="uppercase"
            lts={1}
            mb="md"
            ta="center"
          >
            Profile Signals
          </Text>
          <AdaptScoreRing
            mode="counts"
            signals={session_summary.signal_breakdown}
            centerLabel={primary}
          />
        </Paper>
      )}

      {/* Legacy ADAPT pentagon */}
      {!isNewFormat && adapt_scores && (
        <Paper p="lg" className="glass">
          <Text
            size="xs"
            c="dimmed"
            ff="Orbitron, sans-serif"
            fw={600}
            tt="uppercase"
            lts={1}
            mb="md"
            ta="center"
          >
            ADAPT Framework Scores
          </Text>
          <AdaptScoreRing mode="scores" scores={adapt_scores} />
        </Paper>
      )}

      {/* Response Profile */}
      {isNewFormat ? (
        primary && (
          <ResponseProfileCard
            profile={{ primary_tendency: primary }}
            observation={observed_tendencies}
          />
        )
      ) : (
        <ResponseProfileCard profile={response_profile} />
      )}

      {/* 4. Your Choices (new format only) */}
      {choice_analysis.length > 0 && (
        <Box>
          <SectionHeader>Your Choices</SectionHeader>
          <ChoiceBreakdown choices={choice_analysis} />
        </Box>
      )}

      {/* 5. Text Response Themes (new format only) */}
      {text_input_themes.length > 0 && (
        <Box>
          <SectionHeader>Text Response Themes</SectionHeader>
          <TextInputThemes themes={text_input_themes} />
        </Box>
      )}

      {/* 6. Learning Objectives */}
      {learning_objectives.length > 0 && (
        <Box>
          <SectionHeader>Learning Objectives</SectionHeader>
          <Stack gap="sm">
            {learning_objectives.map((obj, i) => (
              <ObjectiveProgress key={i} objective={obj} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Legacy: Living Skills (shown only for old format) */}
      {!isNewFormat && living_skills.length > 0 && (
        <Box>
          <SectionHeader>Living Skills</SectionHeader>
          <Stack gap="sm">
            {living_skills.map((skill, i) => (
              <LivingSkillCard key={i} skill={skill} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Legacy: AI Narrative */}
      {!isNewFormat && adapt_narrative && (
        <Paper p="lg" className="glass">
          <Text
            size="xs"
            c="dimmed"
            ff="Orbitron, sans-serif"
            fw={600}
            tt="uppercase"
            lts={1}
            mb="md"
          >
            AI Analysis
          </Text>
          <MarkdownRenderer content={adapt_narrative} />
        </Paper>
      )}

      {/* 9. Repeat Comparison */}
      <RepeatComparison comparison={repeat_comparison} />

      {/* 10. CTA */}
      <Paper
        p="lg"
        className="glass"
        style={{
          textAlign: 'center',
          borderColor: 'rgba(0, 229, 255, 0.1)',
        }}
      >
        <Stack gap="md" align="center">
          <Button
            variant="light"
            color="cyan"
            leftSection={<IconChartRadar size={16} />}
            onClick={() => navigate('/profile/analysis')}
          >
            View Your Profile Analysis
          </Button>
          <Text size="xs" c="dimmed">
            Complete more stories to build your cross-session profile
          </Text>

          {sessionId && (
            <>
              <Divider w="60%" color="rgba(0,229,255,0.1)" />
              <Button
                component="a"
                href={`${EXPLORE_BASE_URL}/?session=${sessionId}&token=${(() => { try { return JSON.parse(localStorage.getItem('bw_authToken') || '') } catch { return '' } })()}`}
                target="_blank"
                variant="filled"
                color="lime"
                size="md"
                leftSection={<IconTrendingUp size={18} />}
                fullWidth
                maw={360}
              >
                Explore Your Growth Path
              </Button>
              <Text size="xs" c="dimmed" maw={320}>
                Set goals, adjust focus intensity, and see your projected growth over 12 weeks
              </Text>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  )
}

/* ─── Processing State ─── */

const PROCESSING_STAGES = [
  { threshold: 0, message: 'Collecting your session data...' },
  { threshold: 10, message: 'Analyzing your response patterns...' },
  { threshold: 25, message: 'Building your session debrief...' },
  { threshold: 45, message: 'Generating insights...' },
  { threshold: 60, message: 'This is taking longer than usual...' },
  { threshold: 90, message: 'Still working — the AI analysis may be queued.' },
]

function AnalysisProcessingState({ navigate, refetch }) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(Date.now())

  useEffect(() => {
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000))
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const stage = [...PROCESSING_STAGES].reverse().find((s) => elapsed >= s.threshold)
  const isSlow = elapsed >= 60
  const progress = Math.min((elapsed / 60) * 100, 100)

  return (
    <Container size="md" py="xl">
      <Stack align="center" gap="md" py={60} maw={440} mx="auto">
        {isSlow ? (
          <IconAlertTriangle size={40} color="var(--mantine-color-yellow-5)" />
        ) : (
          <Loader size="md" color="cyan" />
        )}

        <Text fw={600} ff="Orbitron, sans-serif" c={isSlow ? 'yellow.4' : 'cyan.3'}>
          {isSlow ? 'Taking Longer Than Expected' : 'Generating Your Debrief'}
        </Text>

        <Progress
          value={progress}
          color={isSlow ? 'yellow' : 'cyan'}
          size="sm"
          w="100%"
          radius="xl"
          animated={!isSlow}
        />

        <Text c="dimmed" size="sm" ta="center">
          {stage?.message}
        </Text>

        <Text c="dimmed" size="xs" ff="monospace">
          {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')} elapsed
        </Text>

        {isSlow && (
          <Stack gap="xs" align="center" mt="sm">
            <Text c="dimmed" size="xs" ta="center" maw={360}>
              The background analysis job may not be running. You can retry or come back later — your session is saved.
            </Text>
            <Group gap="sm">
              <Button
                variant="light"
                color="gray"
                size="sm"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => navigate('/')}
              >
                Back to Stories
              </Button>
              <Button
                size="sm"
                leftSection={<IconRefresh size={14} />}
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Container>
  )
}

/* ─── Main Page ─── */

export default function AnalysisPage() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { analysis, isLoading, isProcessing, needsUpgrade, error, refetch } = useAnalysis(sessionId)

  // Loading state — inline, never full-page blocker
  if (isLoading) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Loader size="md" color="cyan" />
          <Text c="dimmed" size="sm">Loading your debrief...</Text>
        </Stack>
      </Container>
    )
  }

  // Upgrade required — provisional users need to create an account
  if (needsUpgrade) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60} maw={440} mx="auto">
          <IconBrain size={40} color="var(--mantine-color-cyan-4)" />
          <Text fw={600} ff="Orbitron, sans-serif" c="cyan.3">
            Analysis Locked
          </Text>
          <Text c="dimmed" size="sm" ta="center">
            Create a full account to unlock your personalized session debrief. Your session data is saved and ready to analyze.
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              color="gray"
              size="sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={() => navigate('/')}
            >
              Back to Stories
            </Button>
            <Button
              size="sm"
              onClick={() => navigate('/settings')}
            >
              Create Account
            </Button>
          </Group>
        </Stack>
      </Container>
    )
  }

  // Processing state — analysis is being generated asynchronously
  if (isProcessing) {
    return <AnalysisProcessingState navigate={navigate} refetch={refetch} />
  }

  // Error state
  if (error) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="red.4" fw={600} ff="Orbitron, sans-serif">
            Analysis Unavailable
          </Text>
          <Text c="dimmed" size="sm" ta="center" maw={400}>
            {error.response?.status === 404
              ? 'This analysis has not been generated yet. It will be available once your session is fully processed.'
              : 'Unable to load your analysis. Please try again.'}
          </Text>
          <Group gap="sm">
            <Button
              variant="light"
              color="gray"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/')}
            >
              Back to Stories
            </Button>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </Group>
        </Stack>
      </Container>
    )
  }

  // Empty/no data state
  if (!analysis) {
    return (
      <Container size="md" py="xl">
        <Stack align="center" gap="md" py={60}>
          <Text c="dimmed" size="sm">No analysis data available.</Text>
          <Button
            variant="light"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/')}
          >
            Back to Stories
          </Button>
        </Stack>
      </Container>
    )
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        {/* Back navigation */}
        <Button
          variant="subtle"
          color="gray"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/')}
          style={{ alignSelf: 'flex-start' }}
          size="sm"
        >
          Back to Stories
        </Button>

        {/* Single scrollable debrief */}
        <StoryDebrief analysis={analysis} sessionId={sessionId} />
      </Stack>
    </Container>
  )
}
